import { FILE_TRANSFER_TIMEOUT_MS, publicApiClient } from './client';
import { type WorkflowTemplate } from './workflows';

/**
 * The public customer runtime's entire server surface — two calls.
 *
 * <h3>Why this is a separate module</h3>
 * `workflowsApi` has ~70 methods bound to the authenticated `apiClient`, and `applicationsApi`
 * reaches BMO through a session-refresh wrapper. Neither belongs anywhere near `/ext`, and the
 * cheapest way to guarantee that is for the public page to import a module that cannot express
 * those calls at all. An ESLint `no-restricted-imports` rule on `src/public/**` backs it up,
 * so reaching for the advisor API from the customer page is a build failure rather than a code
 * review catch.
 *
 * <h3>What is deliberately NOT here</h3>
 * No save, no submit, no resume, no upload, no address lookup, no email verification, no
 * session refresh. A page change or a field edit in the public runtime makes no request at
 * all; PDF generation is the only thing the customer's browser sends, and it sends the answers
 * with it. Their answers therefore live in exactly two places — the tab's memory and that
 * request body — which is the cleanest PII posture on the platform (spec §14).
 */
export const publicWorkflowsApi = {
  /**
   * The published workflow to render. Redacted server-side by `ForPublic`, so this carries
   * authored content and nothing about who owns, edits or gates the workflow.
   *
   * 404 for anything not currently published, which reaches this client as a synthetic
   * `ERR_HTML_FOR_JSON` rejection rather than an HTML body — see `publicApiClient`.
   */
  get: async (workflowId: string): Promise<WorkflowTemplate> =>
    (await publicApiClient.get<WorkflowTemplate>(`/public/workflows/${workflowId}`)).data,

  /**
   * Fills one of the workflow's PDFs and returns the bytes.
   *
   * Same `FILE_TRANSFER_TIMEOUT_MS` override as the advisor call, for the same reason
   * (ADR-0028): the response is a document, and a multi-megabyte fill over a domestic
   * connection can outrun the default 30 s while looking exactly like a hung API.
   *
   * `values` is the RAW answer map, including the `inst:<sectionId>:<idx>:<fieldId>` keys
   * repeatable sections produce — not the flattened save snapshot, which would fill every
   * repeatable instance blank.
   */
  generatePdfPreview: async (
    workflowId: string,
    body: { entryId: string; key: string; language: 'en' | 'fr'; values: Record<string, unknown> },
  ): Promise<Blob> =>
    (
      await publicApiClient.post<Blob>(`/public/workflows/${workflowId}/pdf`, body, {
        responseType: 'blob',
        timeout: FILE_TRANSFER_TIMEOUT_MS,
      })
    ).data,
};
